<template>
	<Dropdown :full-width="isXS" :gap="16" placement="end">
		<template #trigger>
			<button
				:title="$t('Menu_menu')"
				class="d-flex gap-2 rounded-pill align-center text-primary-500 cursor-pointer bg-neutral-25"
				:class="type === 'dots' ? 'bg-neutral-500 dots' : ''"
			>
				<Avatar
					v-if="type === 'avatar'"
					:src="profile ? profile.avatar : undefined"
					:width="isXS ? '30px' : '40px'"
					:height="isXS ? '30px' : '40px'"
				/>
				<div v-else class="dots d-flex justify-center rounded-pill align-center justify-center">
					<Icon icon="more" class="text-neutral-25" />
				</div>
			</button>
		</template>
		<template #default="{ close }">
			<div
				id="main-menu-list"
				role="menu"
				class="d-flex flex-column pa-4 rounded-2 overflow-hidden bg-neutral-25"
				:style="{ width: isXS ? '100%' : '290px' }"
			>
				<div v-for="link in links" :key="link.name">
					<a
						role="menuitem"
						:href="link.url"
						class="main-menu-list-item d-flex align-center gap-3 px-4 py-3 rounded-2 text-label-m bg-transparent"
						:class="link.testColorClass ? link.testColorClass : 'text-neutral-800'"
						@click.prevent="(selectOption(link), close())"
					>
						<Icon v-if="!link.endIconPosition" radius="20px" :icon="link.icon!" />
						{{ $t(link.name) }}
						<Icon v-if="link.endIconPosition" radius="20px" :icon="link.icon!" />
					</a>
					<hr v-if="link.hasSeparator" class="main-menu-divider" />
				</div>
			</div>
		</template>
	</Dropdown>
</template>

<script setup lang="ts">
	import { computed, onMounted } from 'vue'
	import { useRouter } from 'vue-router'
	import { AuthActions } from '~/actions/auth/action'
	import CommunicationPreferencesAction from '~/actions/communicationPreferences/action'
	import { ProfileActions } from '~/actions/profile/action'
	import { Profile } from '~/models/Profile'
	import { ProfileNavigation } from '~/services/navigation/profileNavigation'
	import Settings from '~/services/settings'

	type MenuLink = {
		name: string
		url?: string
		externalUrl?: boolean
		targetBlank?: boolean
		icon: string
		hasSeparator?: boolean
		testColorClass?: string
		endIconPosition?: boolean
		callback?: () => void
	}

	const { locale } = useI18n()
	const router = useRouter()

	interface Props {
		isXS: boolean
		profile?: Profile
		isSimple?: boolean
		type?: 'avatar' | 'dots'
	}

	const props = withDefaults(defineProps<Props>(), {
		profile: undefined,
		type: 'avatar',
	})

	defineEmits(['edit-profile'])

	const editDialog = ProfileActions.createEditDialog(props.profile!)
	const defaultLanguage = 'en'

	onMounted(() => {
		editDialog.onAccept(editProfile)
	})

	const links = computed(() => {
		let links = loggedLinks
		if (!props.profile) links = notLoggedLinks
		return links
	})

	const selectOption = (link: MenuLink) => {
		if (!props.profile) AuthActions.redirectToRouteAfterLogin(router.currentRoute.value.fullPath)
		if (link.callback) link.callback()
		if (link.url) goTo(link.url, link.externalUrl, link.targetBlank)
	}

	const goTo = (url: string, external: boolean = false, targetBlank: boolean = false) => {
		if (targetBlank) return window.open(url)
		if (external) return (window.location.href = url)
		return router.push(url)
	}

	const profileEdit = () => {
		editDialog.open({ profile: props.profile! })
	}

	const editProfile = (profile: Profile) => {
		ProfileActions.updateProfile(profile)
	}

	const logout = () => {
		AuthActions.logout()
	}

	const openCommunicationPreferencesDialog = () => {
		const dialog = CommunicationPreferencesAction.createDialog()
		dialog.open()
	}

	const obtainTermsAndConditions = (): string => {
		const correlation: Record<string, string> = {
			es: 'https://passporterapp.com/legal/terminos-y-condiciones',
			en: 'https://passporterapp.com/en/legal/terms-conditions',
		}

		return correlation[locale.value || defaultLanguage] as string
	}

	const obtainPrivacyPolicy = (): string => {
		const correlation: Record<string, string> = {
			es: 'https://passporterapp.com/legal/politica-privacidad',
			en: 'https://passporterapp.com/en/legal/privacy-policy',
		}

		return correlation[locale.value || defaultLanguage] as string
	}

	const loggedLinks: MenuLink[] = [
		{
			name: 'web.header.user_menu.passport',
			url: ProfileNavigation.route,
			icon: 'passport',
			hasSeparator: true,
		},
		{
			name: 'web.header.user_menu.account_settings',
			externalUrl: true,
			icon: 'settings',
			callback: profileEdit,
		},
		{
			name: 'web.header.user_menu.communication_preferences',
			externalUrl: true,
			icon: 'mail',
			callback: openCommunicationPreferencesDialog,
			hasSeparator: true,
		},
		{
			name: 'web.header.user_menu.download_app',
			url: Settings.androidDownloadLink(),
			externalUrl: true,
			targetBlank: true,
			icon: 'app',
			hasSeparator: true,
		},
		{
			name: 'web.header.user_menu.terms_conditions',
			externalUrl: true,
			targetBlank: true,
			icon: 'externalLink',
			endIconPosition: true,
			url: obtainTermsAndConditions(),
		},
		{
			name: 'web.header.user_menu.privacy_policy',
			externalUrl: true,
			targetBlank: true,
			icon: 'externalLink',
			endIconPosition: true,
			url: obtainPrivacyPolicy(),
			hasSeparator: true,
		},

		{
			name: 'web.header.user_menu.log_out',
			url: `/${locale.value}/auth/login`,
			externalUrl: true,
			icon: 'logout',
			testColorClass: 'text-secondary-500',
			callback: logout,
		},
	]

	const notLoggedLinks: MenuLink[] = [
		{
			name: 'R_login_screen_login_button',
			url: `/${locale.value}/auth/login`,
			externalUrl: false,
			icon: 'logoff',
		},
		{
			name: 'R_login_screen_register_button',
			url: `/${locale.value}/auth/signup`,
			externalUrl: false,
			icon: 'twotoneusers',
		},
	]
</script>

<style scoped lang="scss">
	.main-menu-list-item {
		cursor: pointer;
		&:hover {
			background-color: var(--color-primary-25);
		}
	}

	.main-menu-divider {
		display: flex;
		flex-grow: 1;
		border-width: thin 0 0;
		border-style: solid;
		border-color: var(--color-neutral-200);
		margin: 10px 0;
	}
	.dots {
		width: 34px;
		height: 34px;
		opacity: 0.8;
	}
</style>
